import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Trash2, MapPin, FileText, AlertCircle, Calendar, Plus } from "lucide-react";
import PickupDatePicker from "./analytics/PickupDatePicker";

const brand = {
  primary: "#B1336E",
  accentGold: "#E6B43C",
  accentGreen: "#67A43C",
};

export default function Recojo({ setActiveComponent }) {
  const [pickupDate, setPickupDate] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [driverId, setDriverId] = useState(null);
  const [busy, setBusy] = useState(false);

  // Mock data for demo - replace with actual API call
  useEffect(() => {
    const fetchAddresses = async () => {
      const userId = localStorage.getItem("user_id");
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user_addresses/${userId}`);
        const nonArchived = (res.data.addresses || []).filter((a) => !a.archived);
        setAddresses(nonArchived);
      } catch (e) {
        console.error("Error fetching addresses:", e);
      }
    };
    fetchAddresses();
  }, []);

  const driver_1 = ["Chorrillos", "San Juan de Miraflores", "Villa El Salvador", "Villa Maria del Triunfo"];
  const driver_2 = ["Comas", "Independencia", "San Martín de Porres", "Los Olivos", "Rímac"];
  const driver_3 = ["Ate", "Santa Anita", "La Molina", "San Luis"];
  const driver_4 = ["Callao", "Jesús María", "Bellavista", "Lince", "Magdalena del Mar", "Carmen de la Legua", "La Perla", "La Punta", "Cercado de Lima", "San Miguel", "Pueblo Libre", "Breña"];
  const driver_5 = ["Puente Piedra", "Carabayllo", "Ventanilla", "Ancon", "Santa Rosa"];
  const driver_6 = ["San Isidro", "San Borja", "Miraflores", "Surquillo", "Santiago de Surco", "Barranco"];
  const driver_7 = ["El Agustino", "La Victoria", "San Juan de Lurigancho"];

  const handleAddressSelect = (addressId) => {
    const sel = addresses.find((a) => String(a.id) === String(addressId));
    const district = sel?.district || "";
    
    if (driver_1.includes(district)) setDriverId(7);
    else if (driver_2.includes(district)) setDriverId(3);
    else if (driver_3.includes(district)) setDriverId(6);
    else if (driver_4.includes(district)) setDriverId(54);
    else if (driver_5.includes(district)) setDriverId(183);
    else if (driver_6.includes(district)) setDriverId(164);
    else if (driver_7.includes(district)) setDriverId(119);
    else setDriverId(null);

    setSelectedAddress(addressId);
    setErrorMessage("");
  };

  const handleDeleteSelected = async () => {
    if (!selectedAddress) return;
    const ok = window.confirm("¿Estás seguro de eliminar esta dirección?");
    if (!ok) return;

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/archive_address/${selectedAddress}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddresses((prev) => prev.filter((a) => String(a.id) !== String(selectedAddress)));
      setSelectedAddress("");
      alert("La dirección ha sido eliminada correctamente.");
    } catch (e) {
      console.error("Error al archivar la dirección:", e.response?.data || e.message);
      alert("Hubo un error al intentar eliminar la dirección.");
    }
  };

  const handleConfirmClick = () => {
    if (!selectedAddress || !pickupDate) {
      setErrorMessage("Selecciona una dirección y una fecha de recojo.");
      return;
    }
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    setIsModalOpen(false);
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token available.");
      return;
    }

    try {
      setBusy(true);
      const payload = {
        user_id: localStorage.getItem("user_id"),
        pickup_date: pickupDate || null,
        notes: additionalNotes,
        address_id: selectedAddress,
        driver_id: driverId,
      };

      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/recojo`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Response from server:", res.data);
      
      // Show success modal after successful submission
      setIsSuccessModalOpen(true);
    } catch (e) {
      console.error("Error creating recojo:", e.response?.data || e.message);
      setErrorMessage("Hubo un error al solicitar el recojo. Por favor intenta de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col overflow-hidden">
      {/* Header - Fixed */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Analytics')}
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Volver</span>
            </button>
            <h1 className="text-lg sm:text-xl font-bold" style={{ color: brand.primary }}>
              Solicitar Recojo
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
          {/* Info Card */}
          <div className="mb-6 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 p-4">
            <p className="text-sm text-slate-700">
              📦 El courier visitará entre <span className="font-semibold" style={{ color: brand.primary }}>09:00–12:00</span> (hora local Lima)
            </p>
          </div>

          {/* Form Card */}
          <div className="rounded-2xl bg-white shadow-lg border border-slate-200">
            <div className="p-6 space-y-6">
              {/* Date Selection using PickupDatePicker */}
              <PickupDatePicker 
                selectedDate={pickupDate}
                onDateChange={setPickupDate}
                accentColor={brand.accentGold}
              />

              {/* Address Selection */}
              <div>
                <label className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-800">
                  <MapPin className="h-4 w-4" style={{ color: brand.primary }} />
                  Dirección de recojo
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedAddress}
                    onChange={(e) => handleAddressSelect(e.target.value)}
                    className="flex-1 rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-opacity-50 transition-all"
                    style={{ focusBorderColor: brand.primary }}
                  >
                    <option value="">Selecciona una dirección</option>
                    {addresses.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.address}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    type="button"
                    onClick={() => setActiveComponent('MapSelection')}
                    className="flex items-center justify-center rounded-xl px-4 py-3 transition-all bg-green-50 text-green-600 hover:bg-green-100 border-2 border-green-200"
                    title="Agregar nueva dirección"
                  >
                    <Plus className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={!selectedAddress}
                    className={`flex items-center justify-center rounded-xl px-4 py-3 transition-all ${
                      selectedAddress
                        ? "bg-red-50 text-red-600 hover:bg-red-100 border-2 border-red-200"
                        : "bg-slate-100 text-slate-400 border-2 border-slate-200 cursor-not-allowed"
                    }`}
                    title="Eliminar dirección"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-500 flex items-start gap-1">
                  <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  Si no ves tu dirección, haz clic en el botón <span className="font-semibold text-green-600">+</span> para agregar una nueva.
                </p>
              </div>

              {/* Notes */}
              <div>
                <label className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-800">
                  <FileText className="h-4 w-4" style={{ color: brand.accentGreen }} />
                  Notas adicionales
                  <span className="text-xs font-normal text-slate-500">(opcional)</span>
                </label>
                <textarea
                  rows={3}
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Ej: Portón gris al lado de la farmacia, preguntar por Juan..."
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-opacity-50 transition-all resize-none"
                  style={{ focusBorderColor: brand.accentGreen }}
                  maxLength={500}
                />
                <p className="mt-1 text-xs text-slate-400 text-right">
                  {additionalNotes.length}/500
                </p>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Fixed */}
      <div className="bg-white border-t border-slate-200 shadow-lg">
        <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6">
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => setActiveComponent('Analytics')}
              className="rounded-xl border-2 border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmClick}
              disabled={busy}
              className="rounded-xl px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ backgroundColor: brand.primary }}
            >
              {busy ? "Procesando..." : "Confirmar Recojo"}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: `${brand.primary}20` }}>
                  <Calendar className="h-6 w-6" style={{ color: brand.primary }} />
                </div>
                <h3 className="text-lg font-bold" style={{ color: brand.primary }}>
                  Confirmar Solicitud
                </h3>
              </div>
              
              <p className="text-sm text-slate-600 mb-6">
                ¿Está seguro de que la información ingresada para el recojo es correcta?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 rounded-xl border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Revisar
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
                  style={{ backgroundColor: brand.primary }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <Calendar className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-green-600">
                  ¡Recojo Solicitado!
                </h3>
              </div>
              
              <p className="text-sm text-slate-600 mb-6">
                Tu solicitud de recojo ha sido registrada exitosamente. ¿Qué deseas hacer ahora?
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setActiveComponent('Entrega')}
                  className="w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
                  style={{ backgroundColor: brand.primary }}
                >
                  Agregar Entrega
                </button>
                <button
                  onClick={() => setActiveComponent('Analytics')}
                  className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Mis Entregas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}